import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicSocialShare from './PublicSocialShare';

/** Ensure only one <video> plays at a time */
function SingleActiveVideo({ src, poster, className }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onPlay = () =>
      window.dispatchEvent(new CustomEvent('m2t:videoPlay', { detail: el }));
    const onSomeoneElsePlayed = (e) => {
      if (e.detail !== el && !el.paused) el.pause();
    };
    const onHide = () => { if (document.hidden && !el.paused) el.pause(); };

    el.addEventListener('play', onPlay);
    window.addEventListener('m2t:videoPlay', onSomeoneElsePlayed);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      el.removeEventListener('play', onPlay);
      window.removeEventListener('m2t:videoPlay', onSomeoneElsePlayed);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, []);

  return (
    <video
      ref={ref}
      controls
      playsInline
      preload="metadata"
      src={src}
      poster={poster}
      className={className}
      // help some browsers keep the control bar visible
      style={{ display: 'block', backgroundColor: '#000' }}
    />
  );
}

export default function BlogPostCard({ id, post }) {
  const navigate = useNavigate();
  const { fileUrl, fileType, caption, createdAt, thumbnailUrl } = post;

  const dateObj =
    createdAt?.toDate?.() ||
    (createdAt?.seconds ? new Date(createdAt.seconds * 1000) : null);
  const formattedDate = dateObj ? dateObj.toLocaleDateString('en-GB') : '';

  const isImage = (t = '') => t.startsWith('image/');
  const isVideo = (t = '') => t.startsWith('video/');
  const isPdf   = (t = '') => t === 'application/pdf' || /\.pdf(\?|$)/i.test(t);

  // Public URL for sharing
  const publicUrl = `${window.location.origin}/blog/${id}`;

  return (
    <div className="bg-black border border-gray-700 rounded-xl text-white shadow-md mx-4 p-4 mb-8">
      {/* MEDIA: 1080x1350 → 4:5 aspect. No absolute/overflow; center content. */}
      {(thumbnailUrl || fileUrl) && (
        <div className=" border border-gray-900 bg-black">
          <div className="w-full aspect-[4/5] flex items-center justify-center">
            {/* PRIORITIZE VIDEO if this post is a video */}
            {isVideo(fileType) ? (
              <SingleActiveVideo
                src={fileUrl}
                poster={thumbnailUrl || undefined}
                className="max-h-full max-w-full h-auto w-auto"
              />
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={caption || 'Post thumbnail'}
                className="max-h-full max-w-full h-auto w-auto"
                loading="lazy"
              />
            ) : isImage(fileType) ? (
              <img
                src={fileUrl}
                alt={caption || 'Post image'}
                className="max-h-full max-w-full h-auto w-auto"
                loading="lazy"
              />
            ) : isPdf(fileType) ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 underline"
              >
                📄 View PDF
              </a>
            ) : null}
          </div>
        </div>
      )}

      {caption && (
        <p className="mt-3 text-lg font-semibold text-yellow-400">{caption}</p>
      )}

      {formattedDate && (
        <p className="text-sm text-gray-400 mt-2">{formattedDate}</p>
      )}

      {/* Social share */}
      <PublicSocialShare url={publicUrl} title={caption || 'Million2Trillion Blog'} />

      <button
        onClick={() => navigate(`/blog/${id}`)}
        className="mt-6 w-full bg-purple-600 hover:bg-purple-700 py-2 rounded-lg text-white font-semibold"
      >
        View Full Post
      </button>
    </div>
  );
}
